import { Pricing } from '~types/pricing.type';
import {
  CANCELABLE_PERIOD_STATES,
  LIVE_PERIOD_STATES,
  UserSubscriptionPeriod,
} from '~types/subscription.type';

type LocalizeText = (
  textId: string,
  defaultText: string,
  textValues?: Record<string, unknown>,
) => string;

const MINUTES_PER_UNIT: Record<string, number> = {
  SECONDS: 1 / 60,
  MINUTES: 1,
  HOURS: 60,
  DAYS: 1_440,
};

/**
 * Length of one pass period in whole minutes — the backend's
 * `PricingEntity.maxTimeMinutes`, the same figure `periodDurationSeconds`
 * stamps onto every period's `endAt`. `0` when the pricing carries no period.
 *
 * `unit`/`unitQuantity` look tempting but are the price's billing increment;
 * the backend never reads them to size a period.
 */
const getPassPeriodMinutes = (pricing: Pricing | null | undefined): number => {
  const perUnit =
    MINUTES_PER_UNIT[pricing?.maxRentTimeUnit?.toUpperCase() ?? ''];

  return perUnit ? Math.floor((pricing?.maxRentTime ?? 0) * perUnit) : 0;
};

/**
 * How long one period of a pass lasts, e.g. "Month" for a 30-day recurring
 * pass. `null` when the pricing carries no period, so the caller can show the
 * bare price rather than a label with nothing in it.
 *
 * Named by the backend's own rules so the label agrees with the pass title:
 * - recurring (`SubscriptionBillingCycle.of`): under a day counts hours; whole
 *   multiples of 30 days are months, then of 7 days weeks, otherwise days.
 * - one-off (`PricingFormatter.buildTitle`, "30-Day Pass"): hours under a day,
 *   days after, never rolled up into weeks or months.
 *
 * The one deliberate difference is a period under an hour: the backend floors
 * it to "0 hours", so it is counted in minutes here instead.
 */
export const getBillingCycleLabel = (
  pricing: Pricing | null | undefined,
  lt: LocalizeText,
): string | null => {
  const minutes = getPassPeriodMinutes(pricing);
  const base = 'app.rentalPage.newStepThree.pass.cycle';

  const label = (
    count: number,
    singular: [key: string, fallback: string],
    plural: [key: string, fallback: string],
  ) =>
    count === 1
      ? lt(`${base}.${singular[0]}`, singular[1])
      : lt(`${base}.${plural[0]}`, plural[1], { count });

  if (minutes <= 0) {
    return null;
  }

  if (minutes < 60) {
    return label(
      minutes,
      ['minute', 'Minute'],
      ['minutes', '{{count}} minutes'],
    );
  }

  const hours = Math.floor(minutes / 60);
  if (hours < 24) {
    return label(hours, ['hour', 'Hour'], ['hours', '{{count}} hours']);
  }

  const days = Math.floor(hours / 24);
  if (pricing?.autoRenew) {
    if (days % 30 === 0) {
      return label(
        days / 30,
        ['month', 'Month'],
        ['months', '{{count}} months'],
      );
    }

    if (days % 7 === 0) {
      return label(days / 7, ['week', 'Week'], ['weeks', '{{count}} weeks']);
    }
  }

  return label(days, ['day', 'Day'], ['days', '{{count}} days']);
};

/**
 * Whether the swap allowance for the running cycle is exhausted.
 *
 * `maxSwaps === -1` means unlimited, and `swapsLeft` is authoritative —
 * `maxSwaps - usedChargers` is off by one.
 */
export const isSwapLimitReached = (
  subscription: { maxSwaps: number; swapsLeft: number } | null | undefined,
): boolean =>
  !!subscription && subscription.maxSwaps !== -1 && subscription.swapsLeft <= 0;

/**
 * The newest period that actually entitles the user to anything right now.
 *
 * `/api/user-pass/detail` returns periods newest-first, and the newest one is
 * not necessarily live: through a failed renewal it is a `REQUESTED` period
 * whose charge has not landed. Taking it at face value shows a fresh cycle and
 * a full swap allowance to someone whose card is still being retried — so match
 * on state rather than on position.
 *
 * Returns `null` when the pass holds no live period, which is exactly the
 * "renewal pending" situation the caller has to render differently.
 */
export const findLivePeriod = (
  periods: UserSubscriptionPeriod[] | undefined | null,
): UserSubscriptionPeriod | null =>
  periods?.find((period) => LIVE_PERIOD_STATES.includes(period.state)) ?? null;

/**
 * Whether the backend will accept a `CANCEL` on this pass.
 *
 * Keyed on the *latest* period rather than the live one, matching the server's
 * `latestByPassId` check: a pass whose newest period is a renewal still being
 * charged cannot be cancelled until that charge settles. A pass with no periods
 * at all has nothing in flight, so it stays cancellable.
 */
export const isPassCancellable = (
  periods: UserSubscriptionPeriod[] | undefined | null,
): boolean => {
  const latestPeriod = periods?.[0];

  return !latestPeriod || CANCELABLE_PERIOD_STATES.includes(latestPeriod.state);
};

/**
 * Whether the backend will accept a `RENEW` (un-cancel) on a cancelled pass.
 *
 * Mirrors the server's `hasCurrentPaidPeriod`: the *latest* period's `endAt`
 * must still be ahead. The boundary is the end date, not the period's state, so
 * a battery kept past the end (period OVERDUE) does not stretch the window. No
 * periods at all means nothing paid to resume, so it is refused.
 */
export const isPassResubscribable = (
  periods: UserSubscriptionPeriod[] | undefined | null,
): boolean => {
  const latestEndAt = periods?.[0]?.endAt;

  return !!latestEndAt && new Date(latestEndAt).getTime() > Date.now();
};
